import {onUnknownChangeHandler} from 'lib/dom'
import Select from 'lib/ui/Select'
import TextField from 'lib/ui/TextField'
import Skeleton from '@material-ui/lab/Skeleton'
import React from 'react'
import styled from 'styled-components'
import Option from 'lib/ui/Select/Option'
import {spacing} from 'lib/ui/theme'
import {SystemSources, Widget, WidgetSources} from 'Event/Widget/template'
import {
  CodeHolder,
  SubTitle,
  WidgetContainer,
  WidgetTitle,
} from 'organization/Event/WidgetConfig/RedirectWidget'
import CopyButton from 'organization/Event/WidgetConfig/CopyButton'
import {useEvent} from 'Event/EventProvider'

type SourcesFields = 'email' | 'firstName' | 'lastName'

export default function SourcesConfig(props: {
  widget: Widget | null
  updateWidget: (updatedFields: Partial<Widget>) => void
}) {
  const {widget, updateWidget} = props

  const {event} = useEvent()

  if (!widget) {
    return <Skeleton height={200} />
  }

  const handleSystemSourceChange = (source: SystemSources) => {
    if (source === 'click_funnels') {
      updateWidget({
        email_source: 'custom',
        first_name_source: 'custom',
        last_name_source: 'custom',
        system_source: source,
      })
    }

    if (source === 'go_high_level') {
      updateWidget({
        email_source: 'custom',
        first_name_source: 'custom',
        last_name_source: 'custom',
        system_source: source,
      })
    }

    if (source === 'custom') {
      updateWidget({
        email_source: 'url',
        email_source_key: '',
        first_name_source: 'url',
        first_name_source_key: '',
        last_name_source: 'url',
        last_name_source_key: '',
        system_source: source,
      })
    }
  }

  const handleSourceChange = (field: SourcesFields, source: WidgetSources) => {
    if (field === 'email') {
      updateWidget({email_source: source})
      return
    }

    if (field === 'firstName') {
      updateWidget({first_name_source: source})
      return
    }

    if (field === 'lastName') {
      updateWidget({last_name_source: source})
      return
    }
  }

  const handleKeyChange = (field: SourcesFields, key: string) => {
    if (field === 'email') {
      updateWidget({email_source_key: key})
      return
    }

    if (field === 'firstName') {
      updateWidget({first_name_source_key: key})
      return
    }

    if (field === 'lastName') {
      updateWidget({last_name_source_key: key})
      return
    }
  }

  const hasCustomSource =
    widget.email_source === 'custom' ||
    widget.first_name_source === 'custom' ||
    widget.last_name_source === 'custom'

  const customValuesCode = `<!-- Obv.io Widget Custom source Start -->
<script>
    window.addEventListener('load', () => {
        obvioWidget.user({
            email: "USER_EMAIL",
            first_name: "USER_FIRST_NAME",
            last_name: "USER_LAST_NAME",
        })
    })
</script>
<!-- Obv.io Widget Custom source End -->`

  const highLeveCode = `<!-- Obv.io Widget Custom source Start -->
<script>
    window.addEventListener('load', () => {
        var user = JSON.parse(localStorage.getItem('_ud'))
        obvioWidget.user({
            email: user.email,
            first_name: user.first_name,
            last_name: user.last_name,
        })
    })
</script>
<!-- Obv.io Widget Custom source End -->`

  const clickFunnelCode = `<!-- Obv.io Widget Custom source Start -->
<script>
  window.addEventListener('load', () => {

      const get = item => {
          const key = Object.keys(localStorage)
              .find(k => k.startsWith('garlic::') && k.endsWith(':' + item));
          return key ? localStorage.getItem(key) : null;
      };

      let email = get('email');
      let firstName = get('first_name');
      let lastName = get('last_name');

       if (!firstName && !lastName) {
          const fullName = get('name');
          if (fullName) {
              const [fn, ...rest] = fullName.trim().split(' ');
              firstName = fn;
              lastName = rest.join(' ');
          }
      }

      obvioWidget.user({
          email,
          first_name: firstName,
          last_name: lastName
      });

  });
</script>
<!-- Obv.io Widget Custom source End -->`

  const isSourcesDisabled =
    widget.system_source && widget.system_source !== 'custom'

  const sourceCode = () => {
    if (widget.system_source === 'go_high_level') {
      return highLeveCode
    }
    if (widget.system_source === 'click_funnels') {
      return clickFunnelCode
    }
    return customValuesCode
  }

  const defaultSourceSystem = () => {
    if (event.has_click_funnels) {
      return 'click_funnels'
    }

    if (event.has_highlevel) {
      return 'go_high_level'
    }

    return 'custom'
  }

  return (
    <>
      <Select
        onChange={onUnknownChangeHandler(handleSystemSourceChange)}
        value={widget.system_source || defaultSourceSystem()}
        variant="outlined"
        label={'Source System'}
        fullWidth
        aria-label="source system"
      >
        <Option aria-label="cf system source" value="click_funnels">
          Click Funnels
        </Option>
        <Option aria-label="ghl system source" value="go_high_level">
          Go High Level
        </Option>
        <Option aria-label="custom system source" value="custom">
          Custom
        </Option>
      </Select>
      <SourceHolder>
        <Select
          onChange={onUnknownChangeHandler((selected: WidgetSources) => {
            handleSourceChange('email', selected)
          })}
          value={widget.email_source || 'url'}
          variant="outlined"
          label={'Email Source'}
          aria-label="email source"
          disabled={isSourcesDisabled}
        >
          <Option aria-label="email url source" value="url">
            URL parameter
          </Option>
          <Option aria-label="email cookie source" value="cookie">
            Cookies
          </Option>
          <Option aria-label="email local storage source" value="local_storage">
            Local Storage
          </Option>
          <Option
            aria-label="email session storage source"
            value="session_storage"
          >
            Session Storage
          </Option>
          <Option value="custom">Custom</Option>
        </Select>
        <div>
          {widget.email_source !== 'custom' && (
            <TextField
              value={widget.email_source_key}
              variant="outlined"
              label="Email Key (required)"
              inputProps={{
                'aria-label': 'email source key',
              }}
              required
              onChange={(value: string) => {
                handleKeyChange('email', value)
              }}
              disabled={isSourcesDisabled}
            ></TextField>
          )}
          {widget.email_source === 'custom' && (
            <CustomCodeInfo>You can add it with JS code below.</CustomCodeInfo>
          )}
        </div>
      </SourceHolder>
      <SourceHolder>
        <Select
          onChange={onUnknownChangeHandler((selected: WidgetSources) => {
            handleSourceChange('firstName', selected)
          })}
          value={widget.first_name_source || 'url'}
          variant="outlined"
          label={'First Name Source'}
          aria-label="first name source"
          disabled={isSourcesDisabled}
        >
          <Option aria-label="first name url source" value="url">
            URL parameter
          </Option>
          <Option aria-label="first name cookie source" value="cookie">
            Cookies
          </Option>
          <Option
            aria-label="first name local storage source"
            value="local_storage"
          >
            Local Storage
          </Option>
          <Option
            aria-label="first name session storage source"
            value="session_storage"
          >
            Session Storage
          </Option>
          <Option aria-label="first name custom source" value="custom">
            Custom
          </Option>
        </Select>
        <div>
          {widget.first_name_source !== 'custom' && (
            <TextField
              value={widget.first_name_source_key}
              variant="outlined"
              label="First Name Key"
              onChange={(value: string) => {
                handleKeyChange('firstName', value)
              }}
              inputProps={{
                'aria-label': 'first name source key',
              }}
              disabled={isSourcesDisabled}
            ></TextField>
          )}
          {widget.first_name_source === 'custom' && (
            <CustomCodeInfo>You can add it with JS code below.</CustomCodeInfo>
          )}
        </div>
      </SourceHolder>
      <SourceHolder>
        <Select
          onChange={onUnknownChangeHandler((selected: WidgetSources) => {
            handleSourceChange('lastName', selected)
          })}
          value={widget.last_name_source || 'url'}
          variant="outlined"
          label={'Last Name Source'}
          aria-label="last name source"
          disabled={isSourcesDisabled}
        >
          <Option aria-label="last name url source" value="url">
            URL parameter
          </Option>
          <Option aria-label="last name cookie source" value="cookie">
            Cookies
          </Option>
          <Option
            aria-label="last name local storage source"
            value="local_storage"
          >
            Local Storage
          </Option>
          <Option
            aria-label="last name session storage source"
            value="session_storage"
          >
            Session Storage
          </Option>
          <Option aria-label="last name custom source" value="custom">
            Custom
          </Option>
        </Select>
        <div>
          {widget.last_name_source !== 'custom' && (
            <TextField
              value={widget.last_name_source_key}
              variant="outlined"
              label="Last Name Key"
              onChange={(value: string) => {
                handleKeyChange('lastName', value)
              }}
              inputProps={{
                'aria-label': 'last name source key',
              }}
              disabled={isSourcesDisabled}
            ></TextField>
          )}
          {widget.last_name_source === 'custom' && (
            <CustomCodeInfo>You can add it with JS code below.</CustomCodeInfo>
          )}
        </div>
      </SourceHolder>
      {hasCustomSource && (
        <StyledWidgetContainer>
          <WidgetTitle>
            <SubTitle>Code for adding custom source values:</SubTitle>
            <CopyButton value={sourceCode()} />
          </WidgetTitle>
          <CodeHolder>
            <code>{sourceCode()}</code>
          </CodeHolder>
        </StyledWidgetContainer>
      )}
    </>
  )
}

const SourceHolder = styled.div`
  display: flex;
  gap: ${spacing[2]};
  margin-bottom: ${spacing[2]};

  & > div {
    flex: 1;
  }
`

const CustomCodeInfo = styled.div`
  background: #f7f7f7;
  padding: 8px;
  height: 70px;
  display: flex;
  align-items: center;
  justify-content: center;
`

const StyledWidgetContainer = styled(WidgetContainer)`
  margin-top: ${spacing[0]};
`
